<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import { Link2 } from '@lucide/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  CARD_SEO_DATA,
  MAJOR_ARCANA_SLUGS,
  MINOR_ARCANA_SLUGS,
  SUIT_ORDER,
  SUIT_LABELS,
} from '@app/data/cardSeoData';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import PaperLetter from '@app/presentation/components/paper/PaperLetter.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoFaqItem from '@app/presentation/components/seo/SeoFaqItem.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

const TITLE = 'Сочетание карт Таро — значение комбинаций | Таро Мяу';
const DESCRIPTION =
  'Сочетание карт Таро: как комбинации меняют значение расклада. Собрали подсказки по сочетаниям для всех 78 карт — читаем их вместе, просто и честно.';
const URL = 'https://tarotmeow.ru/karty/sochetaniya';

interface ResolvedCombo {
  partnerSlug: string;
  partnerName: string;
  hint: string;
}
interface CardBlock {
  slug: string;
  name: string;
  combos: ResolvedCombo[];
}
interface ComboGroup {
  id: string;
  label: string;
  cards: CardBlock[];
}

// Resolve a card's combinations → partner display names, skipping unknown slugs
// (same rule as CardDetail.cardCombinations) and de-duping repeats within the
// card's own list. Reciprocal pairs across cards are intentionally NOT merged —
// each direction carries its own authored hint written from that card's angle.
function buildBlock(slug: string): CardBlock | null {
  const card = CARD_SEO_DATA[slug];
  if (!card) return null;
  const seen = new Set<string>();
  const combos: ResolvedCombo[] = [];
  for (const combo of card.combinations) {
    const partner = CARD_SEO_DATA[combo.slug];
    if (!partner || seen.has(combo.slug)) continue;
    seen.add(combo.slug);
    combos.push({ partnerSlug: partner.slug, partnerName: partner.name, hint: combo.hint });
  }
  return combos.length ? { slug: card.slug, name: card.name, combos } : null;
}

const isBlock = (b: CardBlock | null): b is CardBlock => b !== null;

// Static import → no reactivity needed; vite-ssg bakes every <a href> into the
// pre-rendered HTML at build time.
const groups: ComboGroup[] = [
  {
    id: 'starshie-arkany',
    label: 'Старшие арканы',
    cards: MAJOR_ARCANA_SLUGS.map(buildBlock).filter(isBlock),
  },
  ...SUIT_ORDER.map((suit) => ({
    id: `mast-${suit}`,
    label: SUIT_LABELS[suit],
    cards: MINOR_ARCANA_SLUGS.filter((s) => CARD_SEO_DATA[s]?.suit === suit)
      .map(buildBlock)
      .filter(isBlock),
  })),
].filter((g) => g.cards.length > 0);

const anchorNav = groups.map((g) => ({ id: g.id, label: g.label }));
// Seeded tilts for the anchor tags (index-based — identical on SSR + client).
const ANCHOR_TILTS = [-1.2, 0.9, -0.6, 1.2, -1];

const totalCombos = groups.reduce(
  (n, g) => n + g.cards.reduce((m, c) => m + c.combos.length, 0),
  0,
);

// The cards that actually rendered a #soch-{slug} anchor — for the ItemList
// JSON-LD. Derived from `groups` (not the raw 78) so an ItemList entry can never
// point at an on-page anchor that doesn't exist, even if a card ever resolves to
// zero combinations. Today this is all 78 cards.
const linkedCards = groups.flatMap((g) => g.cards);

const faq = [
  {
    question: 'Что такое сочетание карт в Таро?',
    answer:
      'Сочетание — это то, как две карты меняют смысл друг друга, когда выпадают рядом. Одна и та же карта в паре с разными соседями звучит по-разному: одна усиливает тему, другая смягчает или переворачивает её. Поэтому расклад читают не по одной карте, а по связям между ними.',
  },
  {
    question: 'Как читать сочетание двух карт?',
    answer:
      'Начни с каждой карты по отдельности — её основное значение. Потом посмотри, что между ними общего, а что спорит: одна усиливает другую, смягчает или задаёт условие. Сочетание — это не сумма двух определений, а настроение, которое рождается, когда карты лежат рядом с твоим вопросом.',
  },
  {
    question: 'Меняет ли перевёрнутая карта значение сочетания?',
    answer:
      'Да, но мягче, чем кажется. Перевёрнутая карта обычно снижает интенсивность, разворачивает энергию внутрь или намекает на задержку — и этим смещает акцент у соседней карты. Смысл пары она не выворачивает наоборот, а скорее приглушает или усложняет.',
  },
  {
    question: 'Нужно ли заучивать все сочетания карт?',
    answer:
      'Нет, и полной таблицы «на все случаи» не существует — комбинаций слишком много, а контекст у каждого вопроса свой. Полезнее понять логику: как карты усиливают и меняют друг друга. А если хочется разобрать именно свой расклад — задай вопрос Таро Коту, он прочитает связи между твоими картами по-человечески.',
  },
];

useHead({
  title: TITLE,
  meta: [
    { name: 'description', content: DESCRIPTION },
    {
      name: 'keywords',
      content:
        'сочетание карт таро, сочетания карт таро, комбинации карт таро, пары карт таро, карты таро вместе, значение сочетаний таро',
    },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: URL },
    { rel: 'alternate', hreflang: 'ru', href: URL },
    { rel: 'alternate', hreflang: 'x-default', href: URL },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Карты', item: 'https://tarotmeow.ru/karty' },
          { '@type': 'ListItem', position: 3, name: 'Сочетания карт', item: URL },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Сочетания карт Таро — комбинации всех 78 карт',
        numberOfItems: linkedCards.length,
        itemListElement: linkedCards.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `Сочетания карты ${c.name}`,
          url: `${URL}#soch-${c.slug}`,
        })),
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      }),
    },
  ],
});
</script>

<template>
  <div class="py-16 md:py-24">
    <div class="site-container">
      <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Карты', to: '/karty' }, { label: 'Сочетания карт' }]" />

      <!-- Hero -->
      <header class="mb-10 text-center md:mb-14">
        <div class="type-overline mb-3.5 text-primary-ink">Комбинации карт</div>
        <h1 class="type-display">Сочетание карт Таро</h1>
        <p class="type-body mx-auto mt-3.5 max-w-2xl text-muted-foreground md:text-[17px]">
          Одна карта задаёт тему, соседняя её меняет — и расклад начинает звучать целиком. Собрали
          подсказки по сочетаниям для всех 78 карт и рассказали, как читать их вместе. Без
          эзотерического тумана.
        </p>
        <div class="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button
            as-child
            size="lg"
          >
            <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
          </Button>
          <Button
            as-child
            variant="outline"
            size="lg"
          >
            <a
              :href="TELEGRAM_BOT_URL"
              target="_blank"
              rel="noopener"
            >{{ TELEGRAM_CTA_LABEL }}</a>
          </Button>
        </div>
      </header>

      <!-- Methodology: a taped note -->
      <ScrollReveal class="mx-auto mb-10 max-w-[820px] md:mb-12">
        <PaperLetter>
          <h2 class="type-subheading text-foreground">Как читать сочетания карт</h2>
          <p class="letter-text type-body mt-2.5 text-muted-foreground">
            Карты в раскладе не живут по отдельности. Каждая — это оттенок, а лежащая рядом его
            усиливает, смягчает или разворачивает. Одна и та же карта в лёгкой паре и в тяжёлой паре
            читается по-разному: сначала лови общее настроение расклада, а потом смотри, как соседи
            влияют друг на друга.
          </p>
          <p class="letter-text type-body mt-3.5 text-muted-foreground">
            Перевёрнутое положение — не автоматический минус. Обычно оно приглушает энергию карты,
            разворачивает её внутрь или говорит о задержке, но не переворачивает смысл механически. В
            сочетании это работает как громкость: перевёрнутая карта звучит тише и смещает акцент у
            той, что рядом, а не отменяет её.
          </p>
          <p class="letter-text type-body mt-3.5 text-muted-foreground">
            И самое честное: готовой формулы «эта карта плюс эта равно вот это» не существует. Значение
            сочетания собирается из твоего вопроса, позиций карт и того, что происходит в жизни прямо
            сейчас. Подсказки ниже — это ориентир, с которого удобно начать, а не приговор.
          </p>
        </PaperLetter>
      </ScrollReveal>

      <!-- Anchor nav: cream tags -->
      <nav class="mb-4 flex flex-wrap justify-center gap-x-2.5 gap-y-3">
        <a
          v-for="(item, i) in anchorNav"
          :key="item.id"
          :href="`#${item.id}`"
          class="seo-tag seo-press paper-tag type-label px-3.5 py-1.5"
          :style="{ '--tilt': `${ANCHOR_TILTS[i % ANCHOR_TILTS.length]}deg` }"
        >{{ item.label }}</a>
      </nav>
      <p class="type-caption mb-12 text-center md:mb-16">
        Всего {{ totalCombos }} сочетаний для 78 карт
      </p>

      <!-- Grouped listing: per card one sheet, its pairs in pencil-ruled rows -->
      <section
        v-for="group in groups"
        :id="group.id"
        :key="group.id"
        class="mb-14 scroll-mt-24 md:mb-20"
      >
        <div class="mb-8 flex items-center gap-2.5">
          <Link2
            class="h-4 w-4 shrink-0 text-primary-ink"
            aria-hidden="true"
          />
          <h2 class="type-title">{{ group.label }}</h2>
        </div>

        <div class="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-2 md:gap-y-12">
          <div
            v-for="block in group.cards"
            :id="`soch-${block.slug}`"
            :key="block.slug"
            class="scroll-mt-24"
          >
            <h3 class="type-heading mb-3.5 text-foreground">
              Сочетания карты
              <RouterLink
                :to="`/karty/${block.slug}`"
                class="seo-link"
              >{{ block.name }}</RouterLink>
            </h3>
            <ul class="seo-lines paper-sheet px-5 md:px-6">
              <li
                v-for="combo in block.combos"
                :key="combo.partnerSlug"
                class="py-4"
              >
                <p class="type-app-heading text-foreground">
                  <RouterLink
                    :to="`/karty/${block.slug}`"
                    class="transition-colors hover:text-primary-ink"
                  >{{ block.name }}</RouterLink>
                  <span class="mx-1.5 text-primary-ink">+</span>
                  <RouterLink
                    :to="`/karty/${combo.partnerSlug}`"
                    class="transition-colors hover:text-primary-ink"
                  >{{ combo.partnerName }}</RouterLink>
                </p>
                <p class="type-body mt-1 text-muted-foreground">{{ combo.hint }}</p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="mb-16 md:mb-24">
        <ScrollReveal class="mb-8 text-center md:mb-10">
          <div class="type-overline mb-3 text-primary-ink">Частые вопросы</div>
          <h2 class="type-title">О сочетаниях карт</h2>
        </ScrollReveal>
        <div class="mx-auto max-w-[820px] space-y-3.5">
          <ScrollReveal
            v-for="(item, i) in faq"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <SeoFaqItem
              :question="item.question"
              :answer="item.answer"
            />
          </ScrollReveal>
        </div>
      </section>

      <!-- Bottom CTA -->
      <ScrollReveal>
        <SeoFinalCta>
          <h2 class="type-title">Разбери свой расклад целиком</h2>
          <p class="type-body mx-auto mt-3.5 max-w-xl text-muted-foreground md:text-[17px]">
            Не хочешь искать пары в таблице — задай вопрос, и Таро Кот прочитает связи между твоими
            картами по-человечески. Бесплатно и без регистрации.
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/karty">Все карты</RouterLink>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </div>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>
